import React, { FC, useState, useEffect } from "react";
import { FaqItem, getAvailableTopics } from "utils/support/faq";
import FaqItemSummary from "./faq-item-summary";
import Topic from "./topic";
import { Box, Text } from "designsystem";
import { useSupportResourceSet } from "hooks/resource-set";
import * as styles from "./styles/faq.module.scss";
import { Pagination } from "components/pagination";
import { isBrowser } from "utils";
import { useLocation } from "@gatsbyjs/reach-router";
import { navigate } from "gatsby";
import SearchContext from "components/search-context";
import AutoComplete from "../site-search/auto-complete";

export interface FaqListProps {
  items: FaqItem[];
  baseUrl: string;
  productSlug: string;
  locale: string;
}
type LabelProps = {
  children: React.ReactNode;
};
const Label: FC<LabelProps> = ({ children }) => (
  <Text weight="bold" size="xxs">
    {children}
  </Text>
);

const FaqList: FC<FaqListProps> = ({ items, baseUrl, productSlug, locale }) => {
  const [paginate, setPaginate] = useState<boolean>(false);
  const [activeTopic, setActiveTopic] = useState<string | undefined>(undefined);
  const [page, setPage] = useState<number>(1);
  const { pathname } = useLocation();

  const [title, topicTitle, numberOfResults] = useSupportResourceSet([
    "support.faq.title",
    "support.faq.topic-filter-title",
    "support.faq.number-of-results"
  ]);
  const availableTopics = getAvailableTopics(items);

  const questionsPerPage = 10;

  const filteredItems = items.filter(({ topics }) =>
    activeTopic ? topics.includes(activeTopic) : true
  );

  const paginatedAndFilteredItems = filteredItems.slice(
    (page - 1) * questionsPerPage,
    (page - 1) * questionsPerPage + questionsPerPage
  );

  const nbPages = Math.ceil(filteredItems.length / questionsPerPage);

  const faqItems = paginate ? paginatedAndFilteredItems : filteredItems;

  const createOnTopicClick = (topic: string | undefined) => () => {
    setActiveTopic(topic);
    setPage(1);
  };

  const createOnPaginationClick = (page: number) => {
    navigate(`${pathname}#faq`);
    setPage(page);
  };

  useEffect(() => {
    // trigger a re-render of the component if we are being rendered on the client
    setPaginate(isBrowser());
  }, []);

  const searchResultsId = `search-results-list`;

  return (
    <Box
      id="faq"
      border="top"
      marginTop="section"
      paddingTop="section"
      direction="column"
    >
      <Text as="h2">{title}</Text>
      <Box hide={["s", "xs"]}>
        <SearchContext
          productSlug={productSlug}
          locale={locale}
          additionalFilter="(tags:support) AND (anchor:faq-title)"
        >
          <AutoComplete type="simple" search="big" />
        </SearchContext>
      </Box>
      {availableTopics.length > 0 && (
        <Box direction="column" marginTop="l">
          <Label>{topicTitle}</Label>
          <Box wrap="wrap">
            {
              <Topic
                onChange={createOnTopicClick(undefined)}
                active={activeTopic === undefined}
              >
                All
              </Topic>
            }
            {availableTopics.map((topic) => (
              <Topic
                key={`topic-${topic}`}
                onChange={createOnTopicClick(topic)}
                active={activeTopic === topic}
              >
                {topic}
              </Topic>
            ))}
          </Box>
        </Box>
      )}
      <Box direction="column" marginTop="3xl">
        {filteredItems.length > 0 && (
          <Label>{`${numberOfResults} ${filteredItems.length}`}</Label>
        )}
        <Box
          id={searchResultsId}
          marginTop="m"
          as="ul"
          direction="column"
          className={styles.faqList}
        >
          {faqItems.map(({ id, question, summary, slug }, index) => (
            <Box as="li" marginTop="m" key={id}>
              <FaqItemSummary
                question={question}
                summary={summary}
                activeTopic={activeTopic}
                resultsLength={filteredItems.length}
                rank={index}
                url={`${baseUrl}${slug}/`}
                page={page}
              />
            </Box>
          ))}
        </Box>
        {paginate && (
          <Pagination
            controls={searchResultsId}
            nbPages={nbPages}
            currentRefinement={page}
            refine={createOnPaginationClick}
          />
        )}
      </Box>
    </Box>
  );
};

export default FaqList;
